import type { CustomsBulkResult, CustomsStatusDto } from '@crm/shared'
import { setCustomsStatus } from '../../shared/api/customs.api'
import { apiErrorText } from '../../shared/api/errorText'
import { pluralPositions } from './customsSelection'

/**
 * Больше позиций за один запрос сервер не примет: смена статуса
 * (`CustomsStatusSchema`) и запрос брокеру (`CreateBrokerRequestSchema`).
 */
export const CUSTOMS_BULK_MAX = 2000

/** Текст, если логист выбрала для файла брокеру больше, чем помещается в один запрос. */
export const BROKER_TOO_MANY_TEXT = `В один файл брокеру — не больше ${CUSTOMS_BULK_MAX} позиций, выберите меньше`

/**
 * Упала не первая порция: часть позиций уже сменила статус. `remainingIds` —
 * позиции, до которых дело не дошло: повторять надо только их, иначе менеджеры
 * по уже изменённым позициям получат уведомление второй раз.
 */
export class PartialBulkError extends Error {
  constructor(
    message: string,
    readonly done: CustomsBulkResult,
    readonly remainingIds: string[],
  ) {
    super(message)
    this.name = 'PartialBulkError'
  }
}

/**
 * Смена статуса нетарифки порциями по {@link CUSTOMS_BULK_MAX}: логист может
 * догрузить очередь и отметить больше позиций, чем сервер берёт за раз.
 * Если упала не первая порция — в ошибке сказано, сколько уже сменилось.
 */
export async function setCustomsStatusInChunks(
  itemIds: string[],
  status: CustomsStatusDto['status'],
  note?: string,
): Promise<CustomsBulkResult> {
  const total: CustomsBulkResult = { updated: 0, skipped: 0 }
  for (let i = 0; i < itemIds.length; i += CUSTOMS_BULK_MAX) {
    const chunk = itemIds.slice(i, i + CUSTOMS_BULK_MAX)
    try {
      const res = await setCustomsStatus({ itemIds: chunk, status, ...(note ? { note } : {}) })
      total.updated += res.updated
      total.skipped += res.skipped
    } catch (e) {
      if (i === 0) throw e
      throw new PartialBulkError(
        `${apiErrorText(e, 'Не удалось сменить статус')}. Уже изменено: ${pluralPositions(total.updated)} — повторите для остальных`,
        total,
        itemIds.slice(i),
      )
    }
  }
  return total
}
